import { useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'

import { type ConfigurationHistoryEvent } from '@/model/firestore/useConfigurationHistory'
import { previousAssignmentQuery } from '@/state/assignments/assignments'
import { type Assignment } from '@/state/assignments/types'

export const useClosestAssignments = (events: ConfigurationHistoryEvent[], regionId: string | undefined) => {
  // Get unique timestamps from events (dedupe to minimize queries)
  const uniqueTimestamps = useMemo(() => {
    const seen = new Set<number>()
    return events
      .filter((e) => {
        const ts = e.timestamp.getTime()
        if (seen.has(ts)) return false
        seen.add(ts)
        return true
      })
      .map((e) => e.timestamp)
  }, [events])

  // Use useQueries to run multiple queries in parallel
  const queries = useQueries({
    queries: uniqueTimestamps.map((timestamp) => ({
      ...previousAssignmentQuery(regionId, timestamp),
      enabled: !!regionId,
    })),
  })

  // Build map: eventTimestamp -> full Assignment (or null)
  const closestAssignments = useMemo(() => {
    const result = new Map<number, Assignment | null>()

    uniqueTimestamps.forEach((timestamp, index) => {
      const queryResult = queries[index]
      if (queryResult.isSuccess && queryResult.data.data.length > 0) {
        result.set(timestamp.getTime(), queryResult.data.data[0])
      } else {
        result.set(timestamp.getTime(), null)
      }
    })

    return result
  }, [uniqueTimestamps, queries])

  const isLoading = queries.some((q) => q.isLoading)

  return { closestAssignments, isLoading }
}
